import { useEffect, useMemo, useState } from "react";
import { LuEye, LuPencil, LuX } from "react-icons/lu";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomModal from "../../../components/CustomModal";
import Input from "../../../components/input";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/DriverPayments.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Payroll.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import { errorNotify, successNotify } from "../../../utils/toast";
import { formatPayrollCurrency, getPayrollStatusClassName } from "./Payroll";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeDriverLine = (row = {}, totalPeriodDays = 0) => {
  const daysWorked = Number(row.days_worked || row.daysWorked || row.days || 0);
  const absentDaysRaw = Number(row.absent_days ?? row.absentDays ?? 0);
  const leaveDaysRaw = Number(row.leave_days ?? row.leaveDays ?? 0);
  const basicSalary = Number(
    row.basic_salary || row.basicSalary || 0,
  );
  const divisor = totalPeriodDays > 0 ? totalPeriodDays : (daysWorked || 30);
  const dailyRate = Number(row.dailyRate || basicSalary / divisor);
  const basePay = Number(row.basePay || (dailyRate * daysWorked) || 0);

  // Calculate aggregate amounts from items if present
  const itemsList = Array.isArray(row.items) ? row.items : [];
  const isArrears = (it) => it.title && it.title.toLowerCase().includes("arrears");

  const sumAllow = itemsList.filter((it) => it.item_type === "allowance" && !isArrears(it)).reduce((s, it) => s + Number(it.amount || 0), 0);
  const sumArrearsPayable = itemsList.filter((it) => it.item_type === "allowance" && isArrears(it)).reduce((s, it) => s + Number(it.amount || 0), 0);
  const sumBonus = itemsList.filter((it) => it.item_type === "bonus").reduce((s, it) => s + Number(it.amount || 0), 0);
  const sumDeduct = itemsList.filter((it) => it.item_type === "deduction" && !isArrears(it)).reduce((s, it) => s + Number(it.amount || 0), 0);
  const sumArrearsReceivable = itemsList.filter((it) => it.item_type === "deduction" && isArrears(it)).reduce((s, it) => s + Number(it.amount || 0), 0);
  const sumShortage = itemsList.filter((it) => it.item_type === "shortage_penalty").reduce((s, it) => s + Number(it.amount || 0), 0);

  const allowances =
    row.allowances !== undefined && itemsList.length === 0
      ? Number(row.allowances || 0)
      : sumAllow;
  const arrearsPayable = itemsList.length > 0 ? sumArrearsPayable : 0;
  const bonuses =
    row.bonuses !== undefined && itemsList.length === 0
      ? Number(row.bonuses || 0)
      : sumBonus;
  const manualDeductions =
    row.deductions !== undefined && itemsList.length === 0
      ? Number(row.deductions || 0)
      : sumDeduct;
  const arrearsReceivable = itemsList.length > 0 ? sumArrearsReceivable : 0;
  const shortagePenalization =
    row.shortage_penalty !== undefined && itemsList.length === 0
      ? Number(row.shortage_penalty || 0)
      : sumShortage;

  const grossPay = basePay + allowances + bonuses + arrearsPayable;
  const totalDeductions = manualDeductions + shortagePenalization + arrearsReceivable;
  const netPay = Number(
    row.net_salary ||
      row.netPay ||
      grossPay - totalDeductions,
  );

  const rawDriverName =
    row.driverName ||
    row.name ||
    row.driver?.username ||
    row.driver?.name ||
    row.driver?.user?.username ||
    `Driver #${row.driver_id || row.id}`;

  return {
    ...row,
    driverName:
      typeof rawDriverName === "string"
        ? rawDriverName
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : rawDriverName,
    daysWorked,
    absentDays: absentDaysRaw,
    leaveDays: leaveDaysRaw,
    dailyRate,
    basePay,
    allowances,
    arrearsPayable,
    bonuses,
    grossPay,
    manualDeductions,
    shortagePenalization,
    arrearsReceivable,
    totalDeductions,
    netPay,
  };
};

const getRunTotals = (rows = [], rec) => {
  const record = rec || {};
  return {
    base:
      rows.reduce((sum, row) => sum + Number(row.basePay || 0), 0) ||
      Number(record.totalSalary || 0),
    allow:
      rows.reduce((sum, row) => sum + Number(row.allowances || 0), 0) ||
      Number(record.allowances || 0),
    bonus:
      rows.reduce((sum, row) => sum + Number(row.bonuses || 0), 0) ||
      Number(record.bonuses || 0),
    deduct:
      rows.reduce(
        (sum, row) =>
          sum +
          Number(row.manualDeductions || 0) +
          Number(row.shortagePenalization || 0),
        0,
      ) || Number(record.deductions || 0),
    net:
      rows.reduce((sum, row) => sum + Number(row.netPay || 0), 0) ||
      Number(record.netPay || 0),
  };
};

const PayrollLineDrawer = ({ line, onClose, onSave, isLocked }) => {
  const [allowances, setAllowances] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (line) {
      const rawItems = Array.isArray(line.items) ? line.items : [];
      const mapItem = (it) => ({
        id: it.id,
        item_type: it.item_type || it.itemType || "allowance",
        title: it.title || "",
        amount: it.amount === "" ? "" : Number(it.amount || 0),
        description: it.description || "",
      });
      setAllowances(
        rawItems.filter((it) => it.item_type === "allowance").map(mapItem),
      );
      setBonuses(
        rawItems.filter((it) => it.item_type === "bonus").map(mapItem),
      );
      setDeductions(
        rawItems.filter((it) => it.item_type === "deduction").map(mapItem),
      );
      setShortages(
        rawItems
          .filter((it) => it.item_type === "shortage_penalty")
          .map(mapItem),
      );
      setFormSubmitted(false);
    } else {
      setAllowances([]);
      setBonuses([]);
      setDeductions([]);
      setShortages([]);
      setFormSubmitted(false);
    }
  }, [line]);

  if (!line) return null;

  // Add handlers
  const addAllowance = () =>
    setAllowances((prev) => [
      ...prev,
      { item_type: "allowance", title: "", amount: "", description: "" },
    ]);
  const addBonus = () =>
    setBonuses((prev) => [
      ...prev,
      { item_type: "bonus", title: "", amount: "", description: "" },
    ]);
  const addDeduction = () =>
    setDeductions((prev) => [
      ...prev,
      { item_type: "deduction", title: "", amount: "", description: "" },
    ]);
  const addShortage = () =>
    setShortages((prev) => [
      ...prev,
      { item_type: "shortage_penalty", title: "", amount: "", description: "" },
    ]);

  // Update handlers
  const updateAllowance = (index, field, value) =>
    setAllowances((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  const updateBonus = (index, field, value) =>
    setBonuses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  const updateDeduction = (index, field, value) =>
    setDeductions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  const updateShortage = (index, field, value) =>
    setShortages((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

  // Remove handlers
  const removeAllowance = (index) =>
    setAllowances((prev) => prev.filter((_, i) => i !== index));
  const removeBonus = (index) =>
    setBonuses((prev) => prev.filter((_, i) => i !== index));
  const removeDeduction = (index) =>
    setDeductions((prev) => prev.filter((_, i) => i !== index));
  const removeShortage = (index) =>
    setShortages((prev) => prev.filter((_, i) => i !== index));

  // Dynamic Summary calculations
  const arrearsAllowItems = allowances.filter(
    (it) => it.title && it.title.toLowerCase().includes("arrears"),
  );
  const regularAllowItems = allowances.filter(
    (it) => !(it.title && it.title.toLowerCase().includes("arrears")),
  );
  const sumArrearsGiven = arrearsAllowItems.reduce(
    (sum, it) => sum + Number(it.amount || 0),
    0,
  );
  const sumRegularAllowances = regularAllowItems.reduce(
    (sum, it) => sum + Number(it.amount || 0),
    0,
  );
  const sumAllowances = sumRegularAllowances + sumArrearsGiven;
  const sumBonuses = bonuses.reduce(
    (sum, it) => sum + Number(it.amount || 0),
    0,
  );
  const sumDeductions = deductions.reduce(
    (sum, it) => sum + Number(it.amount || 0),
    0,
  );
  const sumShortages = shortages.reduce(
    (sum, it) => sum + Number(it.amount || 0),
    0,
  );
  // Deductions offset against arrears first
  const netArrearsAfterDeduction = Math.max(0, sumArrearsGiven - sumDeductions);
  const remainingDeductions = Math.max(0, sumDeductions - sumArrearsGiven);
  const netPayVal =
    line.basePay + sumRegularAllowances + netArrearsAfterDeduction + sumBonuses - remainingDeductions - sumShortages;

  const handleSaveClick = async () => {
    setFormSubmitted(true);

    const allItems = [
      ...allowances.map((it) => ({ ...it, description: "" })),
      ...bonuses.map((it) => ({ ...it, description: "" })),
      ...deductions.map((it) => ({ ...it, description: "" })),
      ...shortages,
    ].map((it) => ({
      item_type: it.item_type,
      title: it.title.trim(),
      amount: it.amount === "" ? "" : Number(it.amount),
      description: it.description ? it.description.trim() : "",
    }));

    // Validation
    const hasEmptyTitle = allItems.some((it) => !it.title);
    const hasEmptyAmount = allItems.some(
      (it) =>
        it.amount === "" || it.amount === null || isNaN(Number(it.amount)),
    );
    const shortageItems = allItems.filter(
      (it) => it.item_type === "shortage_penalty",
    );
    const hasEmptyRemarks = shortageItems.some((it) => !it.description);

    if (hasEmptyTitle || hasEmptyAmount || hasEmptyRemarks) {
      errorNotify("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        payroll_id: Number(line.payroll_id || line.payrollId),
        payroll_driver_id: Number(line.id),
        driver_id: Number(line.driver_id || line.driverId),
        items: allItems,
      };

      await onSave(payload);
    } catch (error) {
      errorNotify(error || "Failed to save payroll items");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="payroll_drawer_overlay">
      <div className="payroll_drawer_scrim" onClick={onClose} />
      <aside
        className="payroll_readonly_drawer"
        style={{ width: "600px", maxWidth: "100%" }}
      >
        <div className="payroll_drawer_header">
          <h3>Configure Payroll - {line.driverName}</h3>
          <button
            type="button"
            className="payroll_drawer_close"
            onClick={onClose}
          >
            <LuX />
          </button>
        </div>

        <div
          className="payroll_drawer_body"
          style={{ height: "calc(100vh - 140px)", overflowY: "auto" }}
        >
          <div className="payroll_drawer_card">
            <h4>Base Salary (read-only)</h4>
            <div className="payroll_readonly_row">
              <span>
                {line.daysWorked} days x {formatPayrollCurrency(line.dailyRate)}
              </span>
              <strong>{formatPayrollCurrency(line.basePay)}</strong>
            </div>
          </div>

          {/* Bonuses / Incentives */}
          <div className="payroll_drawer_card">
            <h4>Bonuses / Incentives</h4>
            {bonuses.map((it, idx) => (
              <div
                className="d-flex align-items-start gap-2 mb-2 w-100"
                key={idx}
              >
                <div style={{ flex: 1.5 }}>
                  <Input
                    label="Title"
                    placeholder="Title"
                    value={it.title}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateBonus(idx, "title", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && !it.title ? "Title is required" : ""
                    }
                    required={true}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Amount"
                    type="number"
                    placeholder="Amount"
                    value={it.amount}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateBonus(idx, "amount", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && (it.amount === "" || it.amount === null)
                        ? "Amount is required"
                        : ""
                    }
                    required={true}
                  />
                </div>
                {!isLocked && (
                  <button
                    type="button"
                    className="cancel_btn p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "38px",
                      height: "38px",
                      minWidth: "38px",
                      borderRadius: "4px",
                      marginTop: "17px",
                    }}
                    onClick={() => removeBonus(idx)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!isLocked && (
              <button
                type="button"
                className="finance_link_button text-primary d-flex align-items-center gap-1 border p-1 px-2 rounded mt-2"
                style={{ fontSize: "13px", background: "#f8f9fa" }}
                onClick={addBonus}
              >
                + Add
              </button>
            )}
          </div>

          {/* Deductions */}
          <div className="payroll_drawer_card">
            <h4>Deductions</h4>
            {sumArrearsGiven > 0 && (
              <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "10px", padding: "8px 10px", background: "#f0fdf4", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                <strong style={{ color: "#16a34a" }}>Arrears: {formatPayrollCurrency(sumArrearsGiven)}</strong> — Deductions will first offset arrears. Driver will receive net arrears after deduction.
              </p>
            )}
            {deductions.map((it, idx) => (
              <div
                className="d-flex align-items-start gap-2 mb-2 w-100"
                key={idx}
              >
                <div style={{ flex: 1.5 }}>
                  <Input
                    label="Title"
                    placeholder="Title"
                    value={it.title}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateDeduction(idx, "title", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && !it.title ? "Title is required" : ""
                    }
                    required={true}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Amount"
                    type="number"
                    placeholder="Amount"
                    value={it.amount}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateDeduction(idx, "amount", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && (it.amount === "" || it.amount === null)
                        ? "Amount is required"
                        : ""
                    }
                    required={true}
                  />
                </div>
                {!isLocked && (
                  <button
                    type="button"
                    className="cancel_btn p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "38px",
                      height: "38px",
                      minWidth: "38px",
                      borderRadius: "4px",
                      marginTop: "17px",
                    }}
                    onClick={() => removeDeduction(idx)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!isLocked && (
              <button
                type="button"
                className="finance_link_button text-primary d-flex align-items-center gap-1 border p-1 px-2 rounded mt-2"
                style={{ fontSize: "13px", background: "#f8f9fa" }}
                onClick={addDeduction}
              >
                + Add
              </button>
            )}
          </div>

          {/* Shortage Penalization */}
          <div className="payroll_drawer_card">
            <h4>Shortage Penalization (manual)</h4>
            <p className="mb-2 text-muted" style={{ fontSize: "12px" }}>
              Manually penalize shortages. Not auto-calculated.
            </p>
            {shortages.map((it, idx) => (
              <div
                className="d-flex align-items-start gap-2 mb-2 w-100"
                key={idx}
              >
                <div style={{ flex: 1.2 }}>
                  <Input
                    label="Title"
                    placeholder="Title"
                    value={it.title}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateShortage(idx, "title", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && !it.title ? "Title is required" : ""
                    }
                    required={true}
                  />
                </div>
                <div style={{ flex: 0.8 }}>
                  <Input
                    label="Amount"
                    type="number"
                    placeholder="Amount"
                    value={it.amount}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateShortage(idx, "amount", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && (it.amount === "" || it.amount === null)
                        ? "Amount is required"
                        : ""
                    }
                    required={true}
                  />
                </div>
                <div style={{ flex: 1.5 }}>
                  <Input
                    label="Remarks"
                    placeholder="Remarks"
                    value={it.description}
                    disabled={isLocked}
                    onChange={(valOrEvent) => {
                      const val =
                        typeof valOrEvent === "object" && valOrEvent.target
                          ? valOrEvent.target.value
                          : valOrEvent;
                      updateShortage(idx, "description", val);
                    }}
                    touched={formSubmitted}
                    error={
                      formSubmitted && !it.description
                        ? "Remarks is required"
                        : ""
                    }
                    required={true}
                  />
                </div>
                {!isLocked && (
                  <button
                    type="button"
                    className="cancel_btn p-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "38px",
                      height: "38px",
                      minWidth: "38px",
                      borderRadius: "4px",
                      marginTop: "17px",
                    }}
                    onClick={() => removeShortage(idx)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {!isLocked && (
              <button
                type="button"
                className="finance_link_button text-primary d-flex align-items-center gap-1 border p-1 px-2 rounded mt-2"
                style={{ fontSize: "13px", background: "#f8f9fa" }}
                onClick={addShortage}
              >
                + Add
              </button>
            )}
          </div>

          <div className="payroll_drawer_card">
            <h4>Calculation Summary</h4>
            <div className="payroll_summary_rows">
              <div>
                <span>Base Pay</span>
                <span>{formatPayrollCurrency(line.basePay)}</span>
              </div>
              {sumRegularAllowances > 0 && (
                <div>
                  <span>+ Allowances</span>
                  <span>{formatPayrollCurrency(sumRegularAllowances)}</span>
                </div>
              )}
              {sumBonuses > 0 && (
                <div>
                  <span>+ Bonuses / Incentives</span>
                  <span>{formatPayrollCurrency(sumBonuses)}</span>
                </div>
              )}
              {sumArrearsGiven > 0 && (
                <>
                  <div style={{ borderTop: "1px dashed #e2e8f0", paddingTop: "8px", marginTop: "4px" }}>
                    <span style={{ color: "#16a34a", fontWeight: 600 }}>Arrears (owed to driver)</span>
                    <span style={{ color: "#16a34a", fontWeight: 600 }}>{formatPayrollCurrency(sumArrearsGiven)}</span>
                  </div>
                  {sumDeductions > 0 && (
                    <div style={{ paddingLeft: "12px" }}>
                      <span style={{ color: "#dc2626" }}>− Deductions (offset vs arrears)</span>
                      <span style={{ color: "#dc2626" }}>−{formatPayrollCurrency(sumDeductions)}</span>
                    </div>
                  )}
                  <div style={{ paddingLeft: "12px", fontWeight: 600 }}>
                    <span style={{ color: netArrearsAfterDeduction > 0 ? "#16a34a" : "#64748b" }}>
                      Net Arrears to Pay
                    </span>
                    <span style={{ color: netArrearsAfterDeduction > 0 ? "#16a34a" : "#64748b" }}>
                      {formatPayrollCurrency(netArrearsAfterDeduction)}
                    </span>
                  </div>
                </>
              )}
              {sumArrearsGiven === 0 && sumDeductions > 0 && (
                <div>
                  <span style={{ color: "#dc2626" }}>− Deductions</span>
                  <span style={{ color: "#dc2626" }}>−{formatPayrollCurrency(sumDeductions)}</span>
                </div>
              )}
              {remainingDeductions > 0 && (
                <div>
                  <span style={{ color: "#dc2626" }}>− Remaining Deductions (from salary)</span>
                  <span style={{ color: "#dc2626" }}>−{formatPayrollCurrency(remainingDeductions)}</span>
                </div>
              )}
              {sumShortages > 0 && (
                <div>
                  <span style={{ color: "#dc2626" }}>− Shortage Penalty</span>
                  <span style={{ color: "#dc2626" }}>−{formatPayrollCurrency(sumShortages)}</span>
                </div>
              )}
            </div>
            <div className="payroll_drawer_net">
              <span>Net Pay</span>
              <strong>{formatPayrollCurrency(netPayVal)}</strong>
            </div>
          </div>
        </div>

        <div className="payroll_drawer_footer d-flex justify-content-end gap-2">
          <button type="button" className="cancel_btn" onClick={onClose}>
            Close
          </button>
          {!isLocked && (
            <button
              type="button"
              className="main_btn"
              disabled={saving}
              onClick={handleSaveClick}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};

const normalizePayrollRecord = (row = {}) => {
  if (!row) return null;
  return {
    id: row.id,
    payrollNo: row.payroll_id ? `PR-${row.payroll_id}` : `PR-${row.id}`,
    monthLabel:
      row.month_name && row.year
        ? `${row.month_name} ${row.year}`
        : row.created_at
          ? new Date(row.created_at).toLocaleString("en-US", {
              month: "long",
              year: "numeric",
            })
          : "N/A",
    periodLabel:
      row.start_at && row.end_at
        ? `${new Date(row.start_at).toLocaleDateString()} - ${new Date(row.end_at).toLocaleDateString()}`
        : row.created_at
          ? `Created: ${new Date(row.created_at).toLocaleDateString()}`
          : "N/A",
    status: row.status || "draft",
    workingDays: row.working_days || 0,
    totalPeriodDays: row.start_at && row.end_at
      ? Math.round((new Date(row.end_at) - new Date(row.start_at)) / (1000 * 60 * 60 * 24)) + 1
      : 0,
    totalSalary: row.total_basic_salary || row.basic_salary || 0,
    allowances: row.total_allowances || row.allowances || 0,
    bonuses: row.total_bonuses || row.bonuses || 0,
    deductions:
      Number(row.total_deductions || row.deductions || 0) +
      Number(row.total_shortage_penalty || row.shortage_penalty || 0),
    netPay: row.total_net_salary || row.net_salary || 0,
    current_approval: row.current_approval || null,
    approvals: row.approvals || [],
  };
};

const PayrollDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [selectedLine, setSelectedLine] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [payrollRecord, setPayrollRecord] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(true);

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(""); // "approve", "reject"
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [viewDriverRow, setViewDriverRow] = useState(null);

  const record = payrollRecord;

  const isLocked = useMemo(() => {
    if (!record) return true;
    return ["locked", "approved"].includes(String(record.status).toLowerCase());
  }, [record]);

  useEffect(() => {
    let isMounted = true;
    const fetchPayrollDetail = async () => {
      if (!id) return;
      setPayrollLoading(true);
      try {
        const response = await apiService.getDriverPayrollDetail(id);
        const data = response?.data || response || {};
        if (isMounted) {
          setPayrollRecord(normalizePayrollRecord(data));
        }
      } catch (error) {
        errorNotify(error || "Failed to fetch payroll details");
      } finally {
        if (isMounted) {
          setPayrollLoading(false);
        }
      }
    };

    fetchPayrollDetail();

    return () => {
      isMounted = false;
    };
  }, [id, refreshTrigger]);

  useEffect(() => {
    let isMounted = true;
    const fetchPayrollDrivers = async () => {
      if (!id) return;
      setDriversLoading(true);
      try {
        const response = await apiService.getPayrollDriversPaginated({
          payroll_id: Number(id),
        });
        const pageData = response?.data || response || {};
        const results = Array.isArray(pageData?.results)
          ? pageData.results
          : Array.isArray(pageData)
            ? pageData
            : [];
        if (isMounted) {
          setDrivers(results);
        }
      } catch (error) {
        errorNotify(error || "Failed to fetch payroll drivers");
      } finally {
        if (isMounted) {
          setDriversLoading(false);
        }
      }
    };

    fetchPayrollDrivers();
    return () => {
      isMounted = false;
    };
  }, [id, refreshTrigger]);

  const driverRows = useMemo(
    () => safeArray(drivers).map((row) => normalizeDriverLine(row, record?.totalPeriodDays)),
    [drivers, record],
  );
  const totals = useMemo(
    () => getRunTotals(driverRows, record),
    [driverRows, record],
  );

  const isApproved = payrollRecord?.current_approval?.status === "approved";
  const isRejected = payrollRecord?.current_approval?.status === "rejected";
  const isActedUpon = isApproved || isRejected;

  const handleOpenApprovalModal = (action) => {
    setApprovalAction(action);
    setApprovalRemarks("");
    setShowApprovalModal(true);
  };

  const handleApprovalActionSubmit = async () => {
    setApprovalSubmitting(true);
    const apiStatus = approvalAction === "approve" ? "approved" : "rejected";
    try {
      const payload = {
        payroll_id: Number(id),
        status: apiStatus,
        comments: approvalRemarks,
      };
      await apiService.approveRejectDriverPayroll(payload);
      successNotify(`Payroll ${apiStatus} successfully`);
      setShowApprovalModal(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || `Failed to ${approvalAction} payroll`);
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleSaveItems = async (payload) => {
    const response = await apiService.upsertPayrollItems(payload);
    successNotify(response?.message || "Payroll items saved successfully");
    setRefreshTrigger((prev) => prev + 1);
    setSelectedLine(null);
  };

  if (payrollLoading) {
    return (
      <div className="payroll_detail_page">
        <Breadcrumbs values={["Finance", "Payroll", "Detail"]} />
        <div className="table_no_records">Loading payroll details...</div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="payroll_detail_page">
        <Breadcrumbs values={["Finance", "Payroll", "Detail"]} />
        <div className="table_no_records">Record not found</div>
      </div>
    );
  }

  return (
    <div className="payroll_detail_page payroll_readonly_detail_page">
      <Breadcrumbs values={["Finance", "Payroll", "Detail"]} />

      <div className="page_header access_view payroll_detail_header">
        <div>
          <h2>
            {record.payrollNo} - {record.monthLabel}
          </h2>
          <p>{record.periodLabel}</p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            cancelBtn={() => navigate("/payroll")}
            shouldRow={false}
            isShowSubmitBtn={false}
            cancelBtnText="Back"
          />
          {payrollRecord?.current_approval && (
            <>
              <button
                type="button"
                className="cancel_btn"
                style={{
                  backgroundColor: isApproved
                    ? "#006b44"
                    : isRejected
                      ? "#f3f4f6"
                      : "#006b44",
                  color: isRejected ? "#9ca3af" : "#ffffff",
                  borderColor: isApproved
                    ? "#006b44"
                    : isRejected
                      ? "#e5e7eb"
                      : "#006b44",
                  fontWeight: 500,
                  fontSize: "14px",
                  padding: "5px 16px",
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isActedUpon ? "not-allowed" : "pointer",
                }}
                onClick={() =>
                  !isActedUpon && handleOpenApprovalModal("approve")
                }
                disabled={isActedUpon}
              >
                {isApproved ? "Approved" : "Approve"}
              </button>
              <button
                type="button"
                className="cancel_btn"
                style={{
                  backgroundColor: isRejected
                    ? "#dc2626"
                    : isApproved
                      ? "#f3f4f6"
                      : "#dc2626",
                  color: isApproved ? "#9ca3af" : "#ffffff",
                  borderColor: isRejected
                    ? "#dc2626"
                    : isApproved
                      ? "#e5e7eb"
                      : "#dc2626",
                  fontWeight: 500,
                  fontSize: "14px",
                  padding: "5px 16px",
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isActedUpon ? "not-allowed" : "pointer",
                }}
                onClick={() =>
                  !isActedUpon && handleOpenApprovalModal("reject")
                }
                disabled={isActedUpon}
              >
                {isRejected ? "Rejected" : "Reject"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="payroll_locked_note">
        <span
          className={`payroll_status_badge is_${getPayrollStatusClassName(record.status)}`}
        >
          {record.status
            ? String(record.status).charAt(0).toUpperCase() +
              String(record.status).slice(1).toLowerCase()
            : ""}
        </span>
        <span>
          Locked payroll cannot be edited; total counts as company-wide salary
          cost in P&amp;L.
        </span>
      </div>


      <div className="payroll_detail_card">
        <h3>Driver Payroll Lines</h3>
        <div className="payroll_detail_table">
          <table className="payroll_plain_table">
            <thead>
              <tr>
                <th>Driver</th>
                <th>Base Salary / Daily Rate</th>
                <th className="th_right">Base Pay</th>
                <th className="th_right">Allowances</th>
                <th className="th_right">Bonuses</th>
                <th className="th_right">Arrears Payable</th>
                <th className="th_right">Gross Pay</th>
                <th className="th_right">Manual Deductions</th>
                <th className="th_right">Shortage Penalization</th>
                <th className="th_right">Arrears Receivable</th>
                <th className="th_right">Total Deductions</th>
                <th className="th_right">Net Pay</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {driversLoading ? (
                <tr>
                  <td
                    colSpan={13}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Loading payroll driver lines...
                  </td>
                </tr>
              ) : driverRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No driver lines found.
                  </td>
                </tr>
              ) : (
                driverRows.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{row.driverName}</td>
                    <td>
                      {formatPayrollCurrency(row.basic_salary)} /{" "}
                      {formatPayrollCurrency(row.dailyRate)}
                    </td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.basePay)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.allowances)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.bonuses)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.arrearsPayable)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.grossPay)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.manualDeductions)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.shortagePenalization)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.arrearsReceivable)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.totalDeductions)}</td>
                    <td style={{ textAlign: "right" }}>{formatPayrollCurrency(row.netPay)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {!isLocked && (
                          <button
                            type="button"
                            className="payroll_line_action"
                            title="Configure (Deductions / Bonuses / Shortage)"
                            onClick={() => setSelectedLine(row)}
                          >
                            <LuPencil />
                          </button>
                        )}
                        <button
                          type="button"
                          className="payroll_line_action"
                          title="View Salary Slip"
                          onClick={() => setViewDriverRow(row)}
                        >
                          <LuEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="payroll_run_totals">
          <span>Run Totals -</span>
          <strong>Base: {formatPayrollCurrency(totals.base)}</strong>
          <strong>Allow: {formatPayrollCurrency(totals.allow)}</strong>
          <strong>Bonus: {formatPayrollCurrency(totals.bonus)}</strong>
          <strong>Deduct: {formatPayrollCurrency(totals.deduct)}</strong>
          <strong className="payroll_net_total">
            Net: {formatPayrollCurrency(totals.net)}
          </strong>
        </div>
      </div>


      {selectedLine && (
        <PayrollLineDrawer
          line={selectedLine}
          isLocked={isLocked}
          onClose={() => setSelectedLine(null)}
          onSave={handleSaveItems}
        />
      )}

      <CustomModal
        show={!!viewDriverRow}
        handleClose={() => setViewDriverRow(null)}
        heading="Salary Slip"
        size="lg"
      >
        {viewDriverRow && (() => {
          const itemsList = Array.isArray(viewDriverRow.items) ? viewDriverRow.items : [];
          const earningItems = itemsList.filter((it) => it.item_type === "allowance" || it.item_type === "bonus");
          const deductionItems = itemsList.filter((it) => it.item_type === "deduction" || it.item_type === "shortage_penalty");
          const totalEarnings = viewDriverRow.basePay + viewDriverRow.allowances + viewDriverRow.bonuses;
          const totalDeductions = viewDriverRow.manualDeductions + viewDriverRow.shortagePenalization;
          const totalDays = record?.totalPeriodDays || 0;
          const presentDays = viewDriverRow.daysWorked || 0;
          const absentDays = viewDriverRow.absentDays || 0;
          const leaveDays = viewDriverRow.leaveDays || 0;

          const parseDays = (desc) => {
            if (!desc) return null;
            const m = String(desc).match(/^(\d+)\s*days?$/i);
            return m ? Number(m[1]) : null;
          };
          const rateCell = (amount, desc) => {
            const days = parseDays(desc);
            if (days && days > 0) return formatPayrollCurrency(Math.round(amount / days)) + "/day";
            return "—";
          };
          const daysCell = (desc) => {
            const days = parseDays(desc);
            if (days) return `${days} days`;
            return desc || "—";
          };

          // Shared styles (th handled via payroll_slip_table CSS class)
          const S = {
            td: { padding: "9px 10px", fontSize: "12px", color: "#132448", borderBottom: "1px solid #f0f2f6", verticalAlign: "middle" },
            tdR: { padding: "9px 10px", fontSize: "12px", color: "#132448", borderBottom: "1px solid #f0f2f6", textAlign: "right", fontWeight: 600, verticalAlign: "middle" },
            sectionRow: { background: "#f4f6fa" },
            sectionTd: { padding: "6px 10px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: "#536176" },
          };

          return (
            <div style={{ fontFamily: "inherit", margin: "-4px" }}>
              {/* Scrollable content */}
              <div style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto", overflowX: "hidden" }}>

              {/* Header */}
              <div style={{ background: "#1450c8", borderRadius: "8px 8px 0 0", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ margin: 0, fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: "rgba(255,255,255,0.6)" }}>Salary Slip</p>
                  <h3 style={{ margin: "4px 0 2px", fontSize: "16px", fontWeight: 700, color: "#fff" }}>{viewDriverRow.driverName}</h3>
                  <p style={{ margin: 0, fontSize: "11px", color: "rgba(255,255,255,0.7)" }}>{record?.periodLabel}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#fff" }}>{record?.monthLabel}</p>
                  <p style={{ margin: "2px 0 4px", fontSize: "11px", color: "rgba(255,255,255,0.65)" }}>{record?.payrollNo}</p>
                  <span style={{ display: "inline-block", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "999px", padding: "2px 8px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", color: "#fff" }}>
                    {record?.status || "Draft"}
                  </span>
                </div>
              </div>

              {/* Attendance grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", borderLeft: "1px solid #e8ecf2", borderRight: "1px solid #e8ecf2", borderBottom: "1px solid #e8ecf2" }}>
                {[
                  { label: "Total Days", value: totalDays || "—", color: "#071735" },
                  { label: "Present", value: presentDays, color: "#16a34a" },
                  { label: "Absent", value: absentDays, color: "#dc2626" },
                  { label: "Leave", value: leaveDays, color: "#d97706" },
                  { label: "Daily Rate", value: formatPayrollCurrency(viewDriverRow.dailyRate), color: "#1450c8" },
                ].map((tile, i) => (
                  <div key={i} style={{ padding: "10px 12px", background: "#f8f9fb", borderRight: i < 4 ? "1px solid #e8ecf2" : "none" }}>
                    <p style={{ margin: 0, fontSize: "9px", color: "#667085", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>{tile.label}</p>
                    <p style={{ margin: "3px 0 0", fontSize: "14px", fontWeight: 700, color: tile.color }}>{tile.value}</p>
                  </div>
                ))}
              </div>

              {/* Earnings + Deductions single table */}
              <div style={{ border: "1px solid #e8ecf2", borderTop: "none" }}>
                <table className="payroll_slip_table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  {/* Earnings section */}
                  <thead>
                    <tr style={S.sectionRow}>
                      <td colSpan={4} style={{ ...S.sectionTd, color: "#16a34a" }}>Earnings</td>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <th className="th_right">Days / Type</th>
                      <th className="th_right">Rate / Day</th>
                      <th className="th_right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}>Base Salary</td>
                      <td style={S.tdR}>{presentDays} days</td>
                      <td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.dailyRate)}/day</td>
                      <td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.basePay)}</td>
                    </tr>
                    {earningItems.length > 0
                      ? earningItems.map((it, i) => (
                          <tr key={i}>
                            <td style={S.td}>{it.title || "Allowance"}</td>
                            <td style={S.tdR}>{daysCell(it.description)}</td>
                            <td style={S.tdR}>{rateCell(it.amount, it.description)}</td>
                            <td style={S.tdR}>{formatPayrollCurrency(it.amount)}</td>
                          </tr>
                        ))
                      : <>
                          {viewDriverRow.allowances > 0 && <tr><td style={S.td}>Allowances</td><td style={S.tdR}>—</td><td style={S.tdR}>—</td><td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.allowances)}</td></tr>}
                          {viewDriverRow.bonuses > 0 && <tr><td style={S.td}>Bonuses</td><td style={S.tdR}>—</td><td style={S.tdR}>—</td><td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.bonuses)}</td></tr>}
                        </>
                    }
                    <tr style={{ background: "#f0fdf4" }}>
                      <td colSpan={3} style={{ ...S.td, fontWeight: 700, color: "#16a34a", borderTop: "2px solid #bbf7d0", borderBottom: "none" }}>Gross Earnings</td>
                      <td style={{ ...S.tdR, fontWeight: 700, color: "#16a34a", borderTop: "2px solid #bbf7d0", borderBottom: "none" }}>{formatPayrollCurrency(totalEarnings)}</td>
                    </tr>

                    {/* Deductions section */}
                    <tr style={S.sectionRow}>
                      <td colSpan={4} style={{ ...S.sectionTd, color: "#dc2626", borderTop: "1px solid #e8ecf2" }}>Deductions</td>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <th className="th_right" colSpan={2}>Type</th>
                      <th className="th_right">Amount</th>
                    </tr>
                    {deductionItems.length > 0
                      ? deductionItems.map((it, i) => (
                          <tr key={i}>
                            <td style={S.td}>{it.title || "Deduction"}</td>
                            <td colSpan={2} style={S.tdR}>{daysCell(it.description)}</td>
                            <td style={S.tdR}>{formatPayrollCurrency(it.amount)}</td>
                          </tr>
                        ))
                      : <>
                          {viewDriverRow.manualDeductions > 0 && <tr><td style={S.td}>Manual Deductions</td><td colSpan={2} style={S.tdR}>—</td><td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.manualDeductions)}</td></tr>}
                          {viewDriverRow.shortagePenalization > 0 && <tr><td style={S.td}>Shortage Penalization</td><td colSpan={2} style={S.tdR}>—</td><td style={S.tdR}>{formatPayrollCurrency(viewDriverRow.shortagePenalization)}</td></tr>}
                          {totalDeductions === 0 && <tr><td colSpan={4} style={{ ...S.td, color: "#9ca3af", fontStyle: "italic" }}>No deductions this period</td></tr>}
                        </>
                    }
                    <tr style={{ background: "#fff5f5" }}>
                      <td colSpan={3} style={{ ...S.td, fontWeight: 700, color: "#dc2626", borderTop: "2px solid #fecaca", borderBottom: "none" }}>Total Deductions</td>
                      <td style={{ ...S.tdR, fontWeight: 700, color: "#dc2626", borderTop: "2px solid #fecaca", borderBottom: "none" }}>{formatPayrollCurrency(totalDeductions)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Net Pay */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#eef3ff", border: "1px solid #c7d7f8", borderTop: "none", borderRadius: "0 0 8px 8px", marginBottom: "4px" }}>
                <div>
                  <p style={{ margin: 0, fontSize: "10px", fontWeight: 700, color: "#1450c8", textTransform: "uppercase", letterSpacing: "0.8px" }}>Net Pay</p>
                  <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#536176" }}>
                    {formatPayrollCurrency(totalEarnings)} − {formatPayrollCurrency(totalDeductions)}
                  </p>
                </div>
                <strong style={{ fontSize: "20px", color: "#1450c8", fontWeight: 700 }}>
                  {formatPayrollCurrency(viewDriverRow.netPay)}
                </strong>
              </div>

              </div>{/* end scrollable */}

              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "12px" }}>
                <button type="button" className="cancel_btn" onClick={() => setViewDriverRow(null)}>Close</button>
              </div>
            </div>
          );
        })()}
      </CustomModal>

      <CustomModal
        show={showApprovalModal}
        handleClose={() => setShowApprovalModal(false)}
        heading={
          approvalAction === "approve" ? "Approve Payroll" : "Reject Payroll"
        }
        size="md"
      >
        <div className="p-3">
          <p
            style={{ fontSize: "14px", color: "#6b7280", marginBottom: "16px" }}
          >
            {approvalAction === "approve"
              ? "Are you sure you want to approve this payroll run?"
              : "Are you sure you want to reject this payroll run?"}
          </p>

          <div className="mb-4">
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: 500,
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              Remarks / Comments
            </label>
            <textarea
              className="form-control"
              style={{
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                padding: "10px",
                minHeight: "100px",
                resize: "vertical",
              }}
              placeholder="Type comments here (optional)..."
              value={approvalRemarks}
              onChange={(e) => setApprovalRemarks(e.target.value)}
            />
          </div>

          <Button
            cancelBtn={() => setShowApprovalModal(false)}
            submitBtn={handleApprovalActionSubmit}
            submitText="Confirm"
            cancelBtnText="Cancel"
            loading={approvalSubmitting}
            disabled={approvalSubmitting}
            buttonExtraCss={
              approvalAction === "approve"
                ? "approve_modal_btn"
                : "reject_modal_btn"
            }
            shouldRow={true}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default PayrollDetails;
